/**
 * What the browser says about itself, for the device registry — Security ›
 * Sessions reads "Chrome on macOS · 140.0.7339.80" from it. Pure ladders
 * over a UA string so they are testable; the `current*` wrappers read the
 * live navigator. Order matters throughout: iPhone/iPad UAs say "like Mac
 * OS X", Android UAs say "Linux", Edge's says "Chrome", Chrome on iOS calls
 * itself CriOS.
 */

export function detectPlatform(ua: string, maxTouchPoints = 0): string {
  if (/iPad/.test(ua)) return "iPadOS";
  if (/iPhone|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return /\bTV\b/.test(ua) ? "Android TV" : "Android";
  if (/CrOS/.test(ua)) return "ChromeOS";
  // iPadOS 13+ Safari sends a desktop "Macintosh" UA; only the touch
  // points give the iPad away.
  if (/Macintosh|Mac OS X/.test(ua)) return maxTouchPoints > 1 ? "iPadOS" : "macOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux|X11/.test(ua)) return "Linux";
  return "unknown";
}

export function detectBrowser(ua: string): string {
  if (ua.includes("Firefox/") || ua.includes("FxiOS/")) return "Firefox";
  if (ua.includes("Edg/") || ua.includes("EdgiOS/")) return "Edge";
  if (ua.includes("Chrome/") || ua.includes("CriOS/")) return "Chrome";
  if (ua.includes("Safari/")) return "Safari";
  return "Browser";
}

const VERSION_TOKEN: Record<string, RegExp> = {
  Firefox: /(?:Firefox|FxiOS)\/([\d.]+)/,
  Edge: /(?:Edg|EdgiOS)\/([\d.]+)/,
  Chrome: /(?:Chrome|CriOS)\/([\d.]+)/,
  Safari: /Version\/([\d.]+)/,
};

/** The browser's version as its UA string states it — "143.0" for Firefox,
 * "18.6" for Safari, but only "140.0.0.0" for Chromium browsers, whose UA
 * strings are frozen (see currentBrowserVersion). */
export function detectBrowserVersion(ua: string): string | undefined {
  return VERSION_TOKEN[detectBrowser(ua)]?.exec(ua)?.[1];
}

/** Client hints brand names, for the full version Chromium withholds from the UA string. */
const HINT_BRAND: Record<string, string> = { Chrome: "Google Chrome", Edge: "Microsoft Edge" };

interface UAData {
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ fullVersionList?: { brand: string; version: string }[] }>;
}

let versionMemo: Promise<string | undefined> | undefined;

/**
 * The browser's full version: on Chromium via the client-hints API (the
 * UA string only says "140.0.0.0"), elsewhere the UA token. Memoised; a
 * browser that offers neither yields undefined, never a throw.
 */
export function currentBrowserVersion(): Promise<string | undefined> {
  versionMemo ??= (async () => {
    const ua = navigator.userAgent;
    const fromUa = detectBrowserVersion(ua);
    try {
      const data = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
      const hints = await data?.getHighEntropyValues?.(["fullVersionList"]);
      const brand = HINT_BRAND[detectBrowser(ua)];
      const full = hints?.fullVersionList?.find((b) => b.brand === brand)?.version;
      if (full) return full;
    } catch {
      // The hint is optional; the UA token stands.
    }
    return fromUa;
  })();
  return versionMemo;
}

export function currentPlatform(): string {
  return detectPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

export function currentBrowser(): string {
  return detectBrowser(navigator.userAgent);
}
